import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useProjectsMultiRegion } from '@/hooks/useAzureResources';
import type { AutomationProject } from '@/types/automation';

interface CurrentProjectResult {
  /** Location (Azure region) from URL params */
  locationName: string | undefined;
  /** Project name from URL params — always defined inside project routes */
  projectName: string | undefined;
  /** Full project object from the cached projects list */
  project: AutomationProject | undefined;
  /** True while the projects list is loading */
  isLoading: boolean;
  /** True when projects have loaded but this project wasn't found */
  notFound: boolean;
  /** The query error, if any */
  error: Error | null;
}

/**
 * URL-param-based project context hook.
 * Reads `projectName` from React Router params and resolves the full project
 * object from the cached projects list.
 *
 * Replaces the old pattern of reading `selectedProject` from `useAppStore()`.
 *
 * Uses `useProjectsMultiRegion(locationName)` so the lookup hits the *regional*
 * control-plane endpoint (e.g. `auto.westcentralus.logic.azure.com`) keyed by
 * region. The previous implementation used `useProjects()`, which read a mutable
 * module-level endpoint set by `RootLayout`'s `useEffect` — on click-through
 * navigation from the dashboard the query fired before the effect could swap
 * endpoints, so the response (from the wrong region) was cached under a key
 * with no region in it, which produced a spurious "Project Not Found".
 */
export function useCurrentProject(): CurrentProjectResult {
  const { locationName, projectName } = useParams<{ tenantId: string; locationName: string; projectName: string }>();
  const { projects, isLoading, errors } = useProjectsMultiRegion(locationName ?? null);

  // Azure resource names are case-insensitive (case-preserving). The URL
  // segment may carry a different casing than what the API returns (links
  // from recent-work history, manually-edited URLs, lowercased redirects,
  // etc.), so compare case-insensitively to avoid spurious "not found".
  const project = useMemo(
    () => projects.find((p) => p.name.toLowerCase() === projectName?.toLowerCase()),
    [projects, projectName],
  );

  // Surface the first per-region error (if any) so consumers can render the
  // generic error UI. `useProjectsMultiRegion` keeps errors per region rather
  // than failing the whole result, which is the right behavior here too.
  const error = useMemo<Error | null>(() => {
    if (errors.size === 0) return null;
    const first = errors.values().next().value;
    return first ?? null;
  }, [errors]);

  // `notFound` must only fire when the project list actually loaded cleanly.
  // If any region query errored, the partial list might be missing the
  // project for *that* reason — `ProjectLayout` checks `notFound` before
  // `error`, so without this gate a transient fetch failure would render
  // a misleading 404 instead of the retryable error UI.
  const notFound = !isLoading && !error && !!projectName && !project;

  return {
    locationName,
    projectName,
    project,
    isLoading,
    notFound,
    error,
  };
}
